import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Database, 
  Filter, 
  BarChart3, 
  MessageSquare, 
  Plug, 
  Settings,
  LogOut,
  Download,
  Table as TableIcon,
  TrendingUp,
  Users,
  MapPin,
  Book
} from 'lucide-react';
import ManualExplorer from '../components/analytics/ManualExplorer';
import AIInsights from '../components/analytics/AIInsights';
import ExternalDB from '../components/analytics/ExternalDB';
import AdminManagement from '../components/analytics/AdminManagement';
import Documentation from './Documentation';

export default function DBAnalytics() {
  const { authUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Allow direct access without auth for now
    setLoading(false);
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading DB Analytics...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'manual', label: 'Manual Explorer', icon: Filter },
    { id: 'ai', label: 'AI Insights', icon: MessageSquare },
    { id: 'external', label: 'External DB', icon: Plug },
    { id: 'docs', label: 'Docs', icon: Book },
    ...(authUser?.admin_level === 'super_admin' ? [
      { id: 'admin', label: 'Admin Management', icon: Users }
    ] : [])
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header - Fixed, Solid, Compact */}
      <header className="bg-slate-800 border-b border-slate-700 fixed top-0 left-0 right-0 z-50 shadow-lg">
        <div className="w-full px-6 py-2 flex items-center justify-between">
          {/* Logo & Title */}
          <div className="flex items-center space-x-3">
            <img 
              src="/assets/images/splink-logo-trnsp.png" 
              alt="Splink Logo" 
              className="h-8 w-auto"
            />
            <div className="border-l border-slate-600 pl-3">
              <h1 className="text-lg font-bold text-white">DB Analytics</h1>
            </div>
          </div>

          {/* Tabs - Centered */}
          <div className="flex items-center space-x-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* User Info & Login - Right Side */}
          {authUser ? (
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-xs font-medium text-white">{authUser?.displayName || authUser?.email}</p>
                <p className="text-xs text-green-400">{authUser?.admin_level === 'super_admin' ? 'Super Admin' : 'Admin'}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
              >
                <LogOut className="w-3 h-3" />
                <span>Logout</span>
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/')}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm"
            >
              Login
            </button>
          )}
        </div>
      </header>

      {/* Main Content - Add padding-top to account for fixed header */}
      <main className="pt-14 max-w-[1920px] mx-auto px-6 py-6">
        {activeTab === 'manual' && <ManualExplorer />}
        {activeTab === 'ai' && <AIInsights />}
        {activeTab === 'external' && <ExternalDB />}
        {activeTab === 'docs' && <Documentation />}
        {activeTab === 'admin' && <AdminManagement currentUser={authUser} />}
      </main>
    </div>
  );
}
